// src/components/admin/decisions/ReturnPaper.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import { 
  Send,
  Mail,
  User,
  FileText,
  X,
  Paperclip,
  AlertCircle,
  CheckCircle,
  Copy,
  Link as LinkIcon,
  ArrowLeft,
  RefreshCw,
  Home
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import axios from '../../utils/axios';

const ReturnPaper = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const quillRef = useRef(null);
  const quillInstance = useRef(null);
  const { user } = useAuth();
  
  // Parse URL parameters
  const queryParams = new URLSearchParams(location.search);
  const manuscriptId = queryParams.get('a');

  // State for manuscript data
  const [manuscriptData, setManuscriptData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  // State for form fields
  const [formData, setFormData] = useState({
    articleId: manuscriptId,
    editor: user?.email || '',
    reviewerEmail: '',
    ccEmail: '',
    bccEmail: '',
    subject: '',
  });

  // State for email tags
  const [ccTags, setCcTags] = useState([]);
  const [bccTags, setBccTags] = useState([]);
  const [ccInput, setCcInput] = useState('');
  const [bccInput, setBccInput] = useState('');

  // State for attachments
  const [attachments, setAttachments] = useState([]);
  const [attachmentPreview, setAttachmentPreview] = useState([]);
  
  // State for UI
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [copySuccess, setCopySuccess] = useState('');

  // Fetch manuscript data
  useEffect(() => {
    const fetchManuscriptData = async () => {
      if (!manuscriptId) {
        setFetchError('No manuscript ID provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await axios.post('/editors/getSubmissionData', { id: manuscriptId });
        
        if (response.data.error) {
          setFetchError(response.data.error);
          setManuscriptData(null);
        } else if (response.data.success && response.data.articles) {
          const article = response.data.articles;
          
          // Parse authors if it's a JSON string
          let authors = [];
          try {
            authors = typeof article.authors === 'string' 
              ? JSON.parse(article.authors) 
              : article.authors || [];
          } catch (e) {
            authors = [article.corresponding_author_fullname];
          }

          const data = {
            id: article.revision_id,
            title: article.title || 'Untitled',
            correspondingAuthor: article.corresponding_author_fullname || '',
            correspondingEmail: article.corresponding_authors_email || '',
            submittedDate: article.created_at || '',
            type: article.article_type || 'Research Article',
            authors: authors
          };
          
          setManuscriptData(data);
          
          // Update form data with fetched information
          setFormData(prev => ({
            ...prev,
            reviewerEmail: data.correspondingEmail,
            subject: `Returned for Correction: ${data.title} (${manuscriptId})`,
          }));
          
          setFetchError(null);
        }
      } catch (error) {
        console.error('Error fetching manuscript:', error);
        setFetchError(error.response?.data?.error || 'Failed to fetch manuscript data');
        setManuscriptData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchManuscriptData();
  }, [manuscriptId]);

  // Initialize Quill editor
  useEffect(() => {
    if (!quillRef.current || !manuscriptData) return;

    // Check if Quill is already initialized
    if (quillInstance.current) {
      return;
    }

    quillInstance.current = new Quill(quillRef.current, {
      modules: {
        toolbar: [
          ['bold', 'italic'],
          ['link', 'blockquote', 'code-block'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ 'header': [1, 2, false] }],
          [{ 'color': [] }],
          ['clean']
        ],
      },
      theme: 'snow',
      placeholder: 'Compose your return for correction letter...',
    });

    // Load initial content
    updateTemplate();

    return () => {
      // Cleanup if needed
    };
  }, [manuscriptData]); // Run when manuscriptData is available

  // Update template when manuscriptData changes
  useEffect(() => {
    if (quillInstance.current && manuscriptData) {
      updateTemplate();
    }
  }, [manuscriptData]);

  const updateTemplate = () => {
    if (!quillInstance.current || !manuscriptData) return;
    
    const template = getFilledTemplate();
    quillInstance.current.setContents(template);
  };

  const getFilledTemplate = () => {
    const today = new Date().toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });

    return [
      { insert: `${today}\n\n` },
      { insert: 'Dear ' },
      { attributes: { color: '#e60000', bold: true }, insert: manuscriptData.correspondingAuthor },
      { insert: `,\n\nYour manuscript "${manuscriptData.title}" (${manuscriptId}) has been successfully submitted online and is presently being given full consideration for publication in African Science Frontiers Initiatives Research Journal (ASFIRJ).\n\nYour manuscript ID is ` },
      { attributes: { color: '#e60000', bold: true }, insert: manuscriptId },
      { insert: `. Please mention the above manuscript ID in all future correspondence with ASFIRJ regarding this submission.\n\nYour paper has now been checked by the Editorial Office to ensure it adheres to ASFIRJ formats and standards, but your paper has several deviations from ASFIRJ requirements as stipulated in the authors' instructions (please carefully read the authors' instructions and ensure that all sections of the manuscript are in line with ASFIRJ requirements here: https://asfirj.org/authors.html).\nThe manuscript has now been returned to your author's dashboard.\n\nBefore the paper can be further processed and sent to a handling editor, we would like you to make the following under-listed amendments and resubmit the manuscript.\n\nAreas to amend in your manuscript:\n\n` },
      { attributes: { color: '#e60000', bold: true }, insert: '[Enter List of Areas of Manuscript To Amend]' },
      { insert: `\n\nSincerely,\n\nASFIRJ Editorial Office\nsubmissions@asfirj.org\n` }
    ];
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle CC email input
  const handleCcKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
      e.preventDefault();
      addCcEmail();
    } else if (e.key === 'Backspace' && ccInput === '' && ccTags.length > 0) {
      removeCcTag(ccTags.length - 1);
    }
  };

  const handleCcBlur = () => {
    if (ccInput.trim()) {
      addCcEmail();
    }
  };

  const addCcEmail = () => {
    const email = ccInput.trim().replace(/,/g, '');
    if (email && isValidEmail(email) && !ccTags.includes(email)) {
      setCcTags([...ccTags, email]);
      setFormData(prev => ({ 
        ...prev, 
        ccEmail: [...ccTags, email].join(', ')
      }));
      setCcInput('');
    }
  };

  // Handle BCC email input
  const handleBccKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
      e.preventDefault();
      addBccEmail();
    } else if (e.key === 'Backspace' && bccInput === '' && bccTags.length > 0) {
      removeBccTag(bccTags.length - 1);
    }
  };

  const handleBccBlur = () => {
    if (bccInput.trim()) {
      addBccEmail();
    }
  };

  const addBccEmail = () => {
    const email = bccInput.trim().replace(/,/g, '');
    if (email && isValidEmail(email) && !bccTags.includes(email)) {
      setBccTags([...bccTags, email]);
      setFormData(prev => ({ 
        ...prev, 
        bccEmail: [...bccTags, email].join(', ')
      }));
      setBccInput('');
    }
  };

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const removeCcTag = (indexToRemove) => {
    const newTags = ccTags.filter((_, index) => index !== indexToRemove);
    setCcTags(newTags);
    setFormData(prev => ({ 
      ...prev, 
      ccEmail: newTags.join(', ')
    }));
  };

  const removeBccTag = (indexToRemove) => {
    const newTags = bccTags.filter((_, index) => index !== indexToRemove);
    setBccTags(newTags);
    setFormData(prev => ({ 
      ...prev, 
      bccEmail: newTags.join(', ')
    }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const totalFiles = attachments.length + files.length;

    if (totalFiles > 10) {
      setMessage({
        type: 'error',
        text: 'Maximum 10 files allowed'
      });
      return;
    }

    setAttachments(prev => [...prev, ...files]);

    // Create preview URLs
    const newPreviews = files.map(file => ({
      name: file.name,
      size: (file.size / 1024).toFixed(2),
      url: URL.createObjectURL(file)
    }));
    setAttachmentPreview(prev => [...prev, ...newPreviews]);
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
    setAttachmentPreview(prev => prev.filter((_, i) => i !== index));
  };

  const handleCopy = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopySuccess(label);
    setTimeout(() => setCopySuccess(''), 2000);
  };

  const handleResetTemplate = () => {
    if (quillInstance.current && manuscriptData) {
      updateTemplate();
      setMessage({
        type: 'success',
        text: 'Template reset with latest data'
      });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setShowConfirmModal(true);
  };

const confirmSend = async () => {
  setSending(true);
  setShowConfirmModal(false);

  try {
    // Get editor content - send as the raw Delta ops array
    const editorContent = quillInstance.current.getContents();
    
    // Prepare form data
    const formDataToSend = new FormData();
    formDataToSend.append('articleId', formData.articleId);
    formDataToSend.append('reviewerEmail', formData.reviewerEmail);
    formDataToSend.append('ccEmail', formData.ccEmail);
    formDataToSend.append('bccEmail', formData.bccEmail);
    formDataToSend.append('subject', formData.subject);
    
    // Send the entire Delta object, not just stringified
    formDataToSend.append('message', JSON.stringify(editorContent.ops || editorContent));
    
    attachments.forEach(file => {
      formDataToSend.append('attachments[]', file);
    });

    // Send to the endpoint
    const response = await axios.post('/editors/email/returnPaper', formDataToSend, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 60000, // 60 second timeout for large files
    });

    if (response.data.status === 'success') {
      setMessage({
        type: 'success',
        text: response.data.message || 'Return for correction letter sent successfully'
      });

      setTimeout(() => {
        setMessage({ type: '', text: '' });
        navigate('/editors/dashboard');
      }, 3000);
    } else if (response.data.status === 'warning') {
      setMessage({
        type: 'warning',
        text: response.data.message || 'Email already sent'
      });
      setSending(false);
    } else {
      setMessage({
        type: 'error',
        text: response.data.message || 'Failed to send email'
      });
      setSending(false);
    }

  } catch (error) {
    console.error('Error sending email:', error);
    setMessage({
      type: 'error',
      text: error.response?.data?.message || error.message || 'Failed to send return letter. Please try again.'
    });
    setSending(false);
  }
};

  // Loading state
  if (loading) {
    return (
      <div className="container-fluid px-4 py-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading manuscript data...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error/Not Found state
  if (fetchError || !manuscriptData) {
    return (
      <div className="container-fluid px-4 py-6">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-10 h-10 text-red-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-2">Manuscript Not Found</h3>
            <p className="text-gray-600 mb-6">
              {fetchError || 'No manuscript data found for the provided ID'}
            </p>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-500">
                <strong>Manuscript ID:</strong> {manuscriptId || 'Not provided'}
              </p>
            </div>
            <button
              onClick={() => navigate('/editors/dashboard')}
              className="inline-flex items-center px-6 py-3 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors"
            >
              <Home className="w-5 h-5 mr-2" />
              Return to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid px-4 py-6">
      {/* Message Display */}
      {message.text && (
        <div className={`mb-4 p-4 rounded-lg flex items-center ${
          message.type === 'success' 
            ? 'bg-green-50 border border-green-200 text-green-700' 
            : message.type === 'warning'
            ? 'bg-yellow-50 border border-yellow-200 text-yellow-700'
            : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          {message.type === 'success' ? (
            <CheckCircle className="w-5 h-5 mr-2" />
          ) : (
            <AlertCircle className="w-5 h-5 mr-2" />
          )}
          {message.text}
        </div>
      )}

      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <h3 className="text-2xl font-bold text-gray-800 flex items-center">
            <ArrowLeft className="w-6 h-6 mr-2 text-yellow-600" />
            Return for Correction
          </h3>
          <button
            type="button"
            onClick={handleResetTemplate}
            className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2"
            title="Reset template with latest data"
          >
            <RefreshCw className="w-4 h-4" />
            Reset Template
          </button>
        </div>
        <p className="text-gray-600 mt-1">
          Return manuscript {manuscriptId} for corrections
        </p>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <form onSubmit={handleSubmit} id="sendMail">
          <input type="hidden" name="articleId" value={manuscriptId} readOnly />

          {/* Recipient Section */}
          <div className="mb-6">
            <h4 className="text-lg font-semibold text-gray-800 mb-4">Recipient</h4>
            
            <div className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Corresponding Author Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  name="reviewerEmail"
                  id="email"
                  value={formData.reviewerEmail}
                  onChange={handleInputChange}
                  readOnly
                  required
                />
              </div>

              {/* CC Field with Tags */}
              <div>
                <label htmlFor="ccEmail" className="block text-sm font-medium text-gray-700 mb-1">
                  CC
                </label>
                <div className="flex flex-wrap items-center gap-2 p-2 border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-yellow-500 focus-within:border-transparent min-h-[42px]">
                  {ccTags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-1 rounded-md text-sm bg-blue-100 text-blue-700"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeCcTag(index)}
                        className="ml-1 hover:text-blue-900"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                  <input
                    type="text"
                    className="flex-1 min-w-[200px] outline-none border-0 p-1 text-sm"
                    value={ccInput}
                    onChange={(e) => setCcInput(e.target.value)}
                    onKeyDown={handleCcKeyDown}
                    onBlur={handleCcBlur}
                    placeholder={ccTags.length === 0 ? "Enter CC emails (comma separated)" : ""}
                  />
                </div>
              </div>

              {/* BCC Field with Tags */}
              <div>
                <label htmlFor="bccEmail" className="block text-sm font-medium text-gray-700 mb-1">
                  BCC
                </label>
                <div className="flex flex-wrap items-center gap-2 p-2 border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-yellow-500 focus-within:border-transparent min-h-[42px]">
                  {bccTags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-1 rounded-md text-sm bg-gray-100 text-gray-700"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeBccTag(index)}
                        className="ml-1 hover:text-gray-900"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                  <input
                    type="text"
                    className="flex-1 min-w-[200px] outline-none border-0 p-1 text-sm"
                    value={bccInput}
                    onChange={(e) => setBccInput(e.target.value)}
                    onKeyDown={handleBccKeyDown}
                    onBlur={handleBccBlur}
                    placeholder={bccTags.length === 0 ? "Enter BCC emails (comma separated)" : ""}
                  />
                </div>
              </div>

              <p className="text-xs text-gray-500">* Press Enter, Space, or Comma to add email addresses</p>
            </div>

            {/* Copyable Information */}
            {manuscriptData && (
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h5 className="text-sm font-medium text-blue-800 mb-3 flex items-center">
                  <LinkIcon className="w-4 h-4 mr-2" />
                  Click to Copy
                </h5>
                <ul className="space-y-2">
                  <li>
                    <button
                      type="button"
                      onClick={() => handleCopy(`${manuscriptData.title} (${manuscriptId})`, 'title')}
                      className="text-sm text-blue-700 hover:text-blue-900 hover:underline flex items-center"
                    >
                      <FileText className="w-3 h-3 mr-2" />
                      Manuscript Title And ID
                      {copySuccess === 'title' && (
                        <span className="ml-2 text-xs text-green-600">Copied!</span>
                      )}
                    </button>
                  </li>
                  <li>
                    <button
                      type="button"
                      onClick={() => handleCopy(manuscriptData.correspondingAuthor, 'author')}
                      className="text-sm text-blue-700 hover:text-blue-900 hover:underline flex items-center"
                    >
                      <User className="w-3 h-3 mr-2" />
                      Corresponding Author's Name
                      {copySuccess === 'author' && (
                        <span className="ml-2 text-xs text-green-600">Copied!</span>
                      )}
                    </button>
                  </li>
                </ul>
              </div>
            )}
          </div>

          {/* Subject Section */}
          <div className="mb-6">
            <h4 className="text-lg font-semibold text-gray-800 mb-4">Subject</h4>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
              name="subject"
              id="subject"
              value={formData.subject}
              onChange={handleInputChange}
              required
            />
          </div>

          {/* Message Body Section */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-semibold text-gray-800">Message Body</h4>
              <div className="text-sm text-gray-600 bg-blue-50 px-3 py-1 rounded-full">
                Auto-filled with manuscript data
              </div>
            </div>
            
            {/* Placeholder Info */}
            {manuscriptData && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 p-4 bg-gray-50 rounded-lg">
                <div className="text-sm">
                  <span className="font-medium text-gray-700">[Manuscript Title]:</span>
                  <span className="ml-2 text-gray-600 truncate block">{manuscriptData.title.substring(0, 30)}...</span>
                </div>
                <div className="text-sm">
                  <span className="font-medium text-gray-700">[Manuscript ID]:</span>
                  <span className="ml-2 text-gray-600">{manuscriptId}</span>
                </div>
                <div className="text-sm">
                  <span className="font-medium text-gray-700">[Corresponding Author]:</span>
                  <span className="ml-2 text-gray-600">{manuscriptData.correspondingAuthor}</span>
                </div>
              </div>
            )}

            {/* Quill Editor */}
            <div className="border border-gray-300 rounded-lg overflow-hidden">
              <div ref={quillRef} style={{ height: '400px' }} />
            </div>
            
            <p className="text-xs text-gray-500 mt-2">
              * The message has been automatically populated with manuscript data. Please replace the red placeholder with your specific instructions.
            </p>
          </div>

          {/* Attachments Section */}
          <div className="mb-6">
            <h4 className="text-lg font-semibold text-gray-800 mb-4">
              Attachments (Max: 10 Files)
            </h4>
            
            <input
              type="file"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
              name="attachments[]"
              id="attachmentInput"
              multiple
              onChange={handleFileChange}
              accept="*/*"
            />

            {/* Attachment Preview */}
            {attachmentPreview.length > 0 && (
              <div className="mt-4 space-y-2" id="attachmentPreviewContainer">
                {attachmentPreview.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center">
                      <Paperclip className="w-4 h-4 text-gray-400 mr-2" />
                      <div>
                        <p className="text-sm font-medium text-gray-700">{file.name}</p>
                        <p className="text-xs text-gray-500">{file.size} KB</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeAttachment(index)}
                      className="p-1 text-gray-400 hover:text-red-500 hover:bg-white rounded"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Submit Button */}
          <div className="flex justify-center mt-8">
            <button
              type="submit"
              disabled={sending}
              className="px-8 py-3 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {sending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Send Return Letter
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                  <ArrowLeft className="w-6 h-6 text-yellow-600" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-gray-900">Confirm Return</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Are you sure you want to return this manuscript for corrections?
                  </p>
                </div>
              </div>

              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  <strong>Note:</strong> The author will need to make corrections before the manuscript can proceed.
                </p>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => setShowConfirmModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmSend}
                  className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors flex items-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Confirm Return
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="footer text-center py-4 border-t border-gray-200 mt-8">
        <p className="text-sm text-gray-500">
          {new Date().getFullYear()} © ASFI - all rights reserved
        </p>
      </footer>
    </div>
  );
};

export default ReturnPaper;